import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle, Bug, Key, GitBranch, GlobeLock,
  Newspaper, ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";

const featureIcons = [Key, Bug, GlobeLock, AlertTriangle, GitBranch, Newspaper];

const FeaturesSection = () => {
  const { t } = useTranslation("home");
  const featureItems = featureIcons.map((Icon, idx) => ({
    icon: <Icon size={20} />,
    title: t(`features.items.${idx}.title`),
    description: t(`features.items.${idx}.description`),
  }));

  return (
    <section className="relative bg-[#020617] text-white py-32 px-6 lg:px-12 overflow-hidden font-sans">
      
      {/* --- Section Divider / Light Beam --- */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-400/50 to-transparent z-20"></div>
      
      {/* FIX 1: Responsive Width for the Top Beam (w-[90%] on mobile, w-[500px] on desktop) */}
      <div className="absolute -top-10 left-1/4 -translate-x-1/2 w-[90%] md:w-[500px] h-[100px] bg-indigo-600/30 blur-[60px] rounded-full pointer-events-none z-10"></div>
      <div className="absolute -top-12 left-1/4 -translate-x-1/2 w-[200px] md:w-[250px] h-[60px] bg-blue-500/20 blur-[40px] rounded-full pointer-events-none z-10"></div>
      
      {/* Background Decor */}
      <div className="absolute inset-0 pointer-events-none">
        {/* FIX 2: Responsive Width/Height for the Background Orb */}
        {/* Changed from fixed w-[600px] to responsive w-[300px] md:w-[600px] */}
        <div className="absolute -top-20 left-0 md:left-1/4 w-[300px] h-[300px] md:w-[600px] md:h-[600px] bg-blue-600/5 blur-[80px] md:blur-[120px] rounded-full opacity-30"></div>
        
        <div 
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#020617]/50 to-[#020617]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="max-w-3xl mb-20">
          <motion.span 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500/10 to-pink-500/10 border border-indigo-500/20 mb-6"
          >
            <span className="bg-gradient-to-r from-indigo-400 to-pink-400 bg-clip-text text-transparent text-m font-bold uppercase tracking-widest">
              {t("features.badge")}
            </span>
          </motion.span>
          
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-bold tracking-tight mb-8 leading-[1.1]"
          >
            {t("features.title")} <br />
            <span className="text-slate-500">{t("features.titleHighlight")}</span>
          </motion.h2>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-slate-300 text-lg leading-relaxed max-w-2xl"
          >
            {t("features.description")}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Features Grid */}
        {/* Features Grid */}
<div className="lg:col-span-6 grid grid-cols-1 md:grid-cols-2 gap-4">
  {featureItems.map((feature, idx) => (
    <motion.div
      key={idx}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.1 }}
      // UPDATED CLASSES:
      // 1. bg-slate-900/80 -> More opaque to separate from background
      // 2. border-white/10 -> Crisper border than slate-800
      // 3. shadow-lg -> Adds depth even without hovering
      className="group relative bg-slate-900/80 backdrop-blur-md border border-white/10 p-6 rounded-2xl transition-all duration-300 hover:border-blue-500/50 hover:bg-slate-900 hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-900/20"
    >
      {/* Hover Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-transparent to-blue-500/0 group-hover:from-blue-500/10 group-hover:to-transparent rounded-2xl transition-all duration-500" />
      
      <div className="relative z-10">
        
        {/* Flex Header */}
        <div className="flex items-center gap-4 mb-4">
            
            {/* Icon Container: Made slightly lighter (bg-slate-800) for contrast against the card */}
            <div className="shrink-0 w-12 h-12 rounded-xl bg-slate-800 border border-white/10 flex items-center justify-center text-slate-300 group-hover:bg-blue-500/20 group-hover:border-blue-500/50 group-hover:text-blue-400 transition-all duration-300">
              {feature.icon}
            </div>

            <h3 className="text-lg font-bold text-slate-100 group-hover:text-white transition-colors">
              {feature.title}
            </h3>
        </div>

        <p className="text-sm text-slate-300 leading-relaxed group-hover:text-slate-300 transition-colors">
          {feature.description}
        </p>
      </div>
    </motion.div>
  ))}
</div>

          {/* Mockup Display */}
          <div className="lg:col-span-6 sticky top-32">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-slate-900/80 backdrop-blur-xl"
            >
              {/* Window Controls */}
              <div className="bg-white/5 px-4 py-3 border-b border-white/5 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-600/50"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-600/50"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-600/50"></div>
                </div>
                <div className="text-[10px] text-slate-500 font-mono ml-4 tracking-widest uppercase">
                  {t("features.platformLabel")}
                </div>
              </div>
              
              {/* Image Container */}
              <div className="relative">
                 <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent opacity-50 z-10" />
                 <img
                    src="/assets/whiteintel-events-mockup-svg.svg"
                    alt="Whiteintel Dashboard"
                    className="w-full h-auto opacity-90"
                    width={800}
                    height={600}
                 />
              </div>
            </motion.div>

            <div className="mt-8 flex items-center justify-end px-2">
              <Link
                to="/platform-tour"
                className="group flex items-center gap-2 text-sm font-bold text-indigo-400 transition-all uppercase tracking-wider hover:text-indigo-300"
              >
                {t("features.platformTour")}
                <ArrowRight size={16} className="text-indigo-400 group-hover:text-pink-400 group-hover:translate-x-1 transition-all" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;